import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, DollarSign, AlertTriangle, Search, Save, RefreshCw, 
  ChevronDown, ChevronUp, Edit2, Check, X, Layers, Scissors,
  TrendingDown, Archive, Calendar, Hash, Plus, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/pos`;

const StockPriceManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stock');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expiredProducts, setExpiredProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Serial number modal state
  const [serialProduct, setSerialProduct] = useState(null);
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [serialLoading, setSerialLoading] = useState(false);
  const [newSerials, setNewSerials] = useState('');
  const [editingSerial, setEditingSerial] = useState(null);
  const [editSerialValue, setEditSerialValue] = useState('');
  
  // Filters
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [stockFilter, setStockFilter] = useState('all'); // all, low_stock, out_of_stock
  
  // Price editing state
  const [editedProducts, setEditedProducts] = useState({});
  
  // Stock editing state
  const [editingStock, setEditingStock] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Batch expansion state
  const [expandedProducts, setExpandedProducts] = useState({});
  const [productBatches, setProductBatches] = useState({});
  const [loadingBatches, setLoadingBatches] = useState({});

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'expired') {
      fetchExpiredProducts();
    } else if (activeTab === 'raw_materials') {
      fetchRawMaterials();
    }
  }, [activeTab]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [productsRes, categoriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/products/enhanced`, { headers }),
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/stock/summary`, { headers })
      ]);
      
      // Filter out raw materials from main products
      setProducts(productsRes.data.filter(p => !p.exception_for_sale));
      setCategories(categoriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRawMaterials(response.data);
    } catch (error) {
      console.error('Error fetching raw materials:', error);
    }
  };

  const fetchExpiredProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/expired-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpiredProducts(response.data);
    } catch (error) {
      console.error('Error fetching expired products:', error);
    }
  };

  const fetchBatches = async (productId) => {
    if (productBatches[productId]) {
      setExpandedProducts(prev => ({ ...prev, [productId]: !prev[productId] }));
      return;
    }
    
    setLoadingBatches(prev => ({ ...prev, [productId]: true }));
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/product-batches/${productId}?include_expired=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductBatches(prev => ({ ...prev, [productId]: response.data }));
      setExpandedProducts(prev => ({ ...prev, [productId]: true }));
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoadingBatches(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Handle price field changes
  const handlePriceChange = (productId, field, value) => {
    setEditedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  // Calculate profit margin
  const calculateMargin = (cost, selling) => {
    if (!cost || cost === 0) return 0;
    return ((selling - cost) / cost * 100).toFixed(1);
  };

  // Save single product prices
  const saveProductPrices = async (product) => {
    const updates = editedProducts[product.id];
    if (!updates) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('pos_token');
      
      const payload = {
        base_price: updates.base_price ?? product.base_price,
        mrp: updates.mrp ?? product.mrp,
        selling_price: updates.selling_price ?? product.selling_price,
        selling_price_l2: updates.selling_price_l2 ?? product.selling_price_l2,
        selling_price_l3: updates.selling_price_l3 ?? product.selling_price_l3,
        selling_price_l4: updates.selling_price_l4 ?? product.selling_price_l4,
        selling_price_l5: updates.selling_price_l5 ?? product.selling_price_l5,
        price_updated_at: new Date().toISOString()
      };
      
      await axios.put(`${API}/products/enhanced/${product.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === product.id ? { ...p, ...payload } : p
      ));
      
      // Clear edited state for this product
      setEditedProducts(prev => {
        const updated = { ...prev };
        delete updated[product.id];
        return updated;
      });
      
      alert('Prices updated successfully!');
    } catch (error) {
      console.error('Error saving prices:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to save prices'));
    } finally {
      setSaving(false);
    }
  };

  // Save all edited products
  const saveAllChanges = async () => {
    const editedIds = Object.keys(editedProducts);
    if (editedIds.length === 0) {
      alert('No changes to save');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('pos_token');
      
      for (const productId of editedIds) {
        const product = products.find(p => p.id === productId);
        if (!product) continue;
        
        const updates = editedProducts[productId];
        const payload = {
          base_price: updates.base_price ?? product.base_price,
          mrp: updates.mrp ?? product.mrp,
          selling_price: updates.selling_price ?? product.selling_price,
          selling_price_l2: updates.selling_price_l2 ?? product.selling_price_l2,
          selling_price_l3: updates.selling_price_l3 ?? product.selling_price_l3,
          selling_price_l4: updates.selling_price_l4 ?? product.selling_price_l4,
          selling_price_l5: updates.selling_price_l5 ?? product.selling_price_l5,
          price_updated_at: new Date().toISOString()
        };
        
        await axios.put(`${API}/products/enhanced/${product.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Refresh data
      await fetchInitialData();
      setEditedProducts({});
      alert(`${editedIds.length} product(s) updated successfully!`);
    } catch (error) {
      console.error('Error saving prices:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to save prices'));
    } finally {
      setSaving(false);
    }
  };

  // Stock editing functions
  const handleEditStock = (product) => {
    setEditingStock(product.id);
    setEditForm({
      current_stock: product.current_stock,
      stock_alert_quantity: product.stock_alert_quantity || 0
    });
  };

  const handleSaveStock = async (productId) => {
    try {
      const token = localStorage.getItem('pos_token');
      await axios.put(`${API}/stock/${productId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, ...editForm } : p
      ));
      setEditingStock(null);
      fetchInitialData(); // Refresh summary
    } catch (error) {
      alert('Error updating stock: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Serial Number Management functions
  const openSerialManager = async (product) => {
    setSerialProduct(product);
    setSerialLoading(true);
    setNewSerials('');
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/serial-numbers/${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSerialNumbers(resp.data || []);
    } catch (err) {
      console.error('Error loading serials:', err);
      setSerialNumbers([]);
    } finally {
      setSerialLoading(false);
    }
  };

  const addSerialNumbers = async () => {
    if (!newSerials.trim() || !serialProduct) return;
    const serials = newSerials.split('\n').map(s => s.trim()).filter(s => s);
    if (serials.length === 0) return;
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/serial-numbers/bulk`, {
        product_id: serialProduct.id,
        serial_numbers: serials
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewSerials('');
      openSerialManager(serialProduct); // Refresh
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add serial numbers');
    }
  };

  const deleteSerial = async (serialId) => {
    if (!window.confirm('Delete this serial number?')) return;
    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/serial-numbers/${serialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      openSerialManager(serialProduct);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const saveSerialEdit = async (serialId) => {
    if (!editSerialValue.trim()) return;
    try {
      const token = localStorage.getItem('pos_token');
      await axios.put(`${API}/serial-numbers/${serialId}`, {
        serial_number: editSerialValue.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEditingSerial(null);
      openSerialManager(serialProduct);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update');
    }
  };

  const availableSerials = serialNumbers.filter(s => s.status === 'available');
  const soldSerials = serialNumbers.filter(s => s.status === 'sold');

  // Filtered products
  const filteredProducts = products.filter(p => {
    const matchesSearch = !search || 
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
    
    let matchesStock = true;
    if (stockFilter === 'low_stock') {
      matchesStock = p.current_stock > 0 && p.current_stock <= (p.stock_alert_quantity || 5);
    } else if (stockFilter === 'out_of_stock') {
      matchesStock = p.current_stock <= 0;
    }
    
    return matchesSearch && matchesCategory && matchesStock;
  });

  const filteredRawMaterials = rawMaterials.filter(p => {
    const matchesSearch = !search || 
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasChanges = Object.keys(editedProducts).length > 0;
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="stock-price-management-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Stock & Price Management</h1>
            <p className="text-slate-500 text-sm">Manage inventory, prices, batches and raw materials</p>
          </div>
        </div>
        
        {activeTab === 'price' && hasChanges && (
          <button
            onClick={saveAllChanges}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
            data-testid="save-all-btn"
          >
            <Save size={18} />
            {saving ? 'Saving...' : `Save All (${Object.keys(editedProducts).length})`}
          </button>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (activeTab === 'stock' || activeTab === 'batch') && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Products</p>
                <p className="text-xl font-bold text-slate-800">{summary.total_products}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Check className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">In Stock</p>
                <p className="text-xl font-bold text-emerald-600">{summary.available_count || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <TrendingDown className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Low Stock</p>
                <p className="text-xl font-bold text-amber-600">{summary.low_stock_count || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <X className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Out of Stock</p>
                <p className="text-xl font-bold text-red-600">{summary.out_of_stock_count || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <DollarSign className="text-violet-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Stock Value</p>
                <p className="text-lg font-bold text-violet-600">₹{(summary.total_stock_value || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
        {[
          { id: 'stock', label: 'All Stock', icon: Package },
          { id: 'batch', label: 'Batch Stock', icon: Layers },
          { id: 'price', label: 'Price Edit', icon: DollarSign },
          { id: 'expired', label: 'Expired', icon: AlertTriangle, badge: expiredProducts.length },
          { id: 'raw_materials', label: 'Raw Materials', icon: Scissors }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? tab.id === 'expired' ? 'bg-red-500 text-white shadow-sm' : 'bg-white text-violet-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.badge > 0 && (
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
              }`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      {(activeTab === 'stock' || activeTab === 'batch' || activeTab === 'price') && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or SKU..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="search-input"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            {activeTab === 'stock' && (
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'low_stock', label: 'Low Stock' },
                  { id: 'out_of_stock', label: 'Out' }
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setStockFilter(filter.id)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      stockFilter === filter.id
                        ? 'bg-white text-violet-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-800'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'stock' && (
        <StockListView 
          products={filteredProducts}
          editingStock={editingStock}
          editForm={editForm}
          setEditForm={setEditForm}
          onEdit={handleEditStock}
          onSave={handleSaveStock}
          onCancel={() => setEditingStock(null)}
          onManageSerials={openSerialManager}
        />
      )}

      {activeTab === 'batch' && (
        <BatchStockView 
          products={filteredProducts.filter(p => p.track_batch_expiry)} 
          allProducts={filteredProducts}
          formatDate={formatDate}
          expandedProducts={expandedProducts}
          productBatches={productBatches}
          loadingBatches={loadingBatches}
          onToggleBatch={fetchBatches}
          today={today}
        />
      )}

      {activeTab === 'price' && (
        <PriceEditView 
          products={filteredProducts}
          editedProducts={editedProducts}
          saving={saving}
          onPriceChange={handlePriceChange}
          onSave={saveProductPrices}
          calculateMargin={calculateMargin}
          formatDateTime={formatDateTime}
        />
      )}

      {activeTab === 'expired' && (
        <ExpiredProductsView 
          expiredProducts={expiredProducts} 
          formatDate={formatDate}
          onRefresh={fetchExpiredProducts}
        />
      )}

      {activeTab === 'raw_materials' && (
        <RawMaterialsView 
          materials={filteredRawMaterials}
          search={search}
          setSearch={setSearch}
        />
      )}

      {/* Serial Number Manager Modal */}
      {serialProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="serial-number-modal">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Serial Numbers</h3>
                <p className="text-sm text-slate-500">{serialProduct.name} (Stock: {serialProduct.current_stock})</p>
              </div>
              <button onClick={() => { setSerialProduct(null); setEditingSerial(null); }} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
            </div>
            
            <div className="p-4 border-b border-slate-200">
              <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">Add Serial Numbers (one per line)</label>
              <textarea
                value={newSerials}
                onChange={e => setNewSerials(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none"
                placeholder="Enter serial numbers, one per line..."
                data-testid="serial-input"
              />
              <button
                onClick={addSerialNumbers}
                disabled={!newSerials.trim()}
                className="mt-2 flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-40"
                data-testid="add-serials-btn"
              >
                <Plus size={14} /> Add Serials
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {serialLoading ? (
                <div className="flex justify-center py-6"><RefreshCw className="animate-spin text-orange-500" size={24} /></div>
              ) : serialNumbers.length === 0 ? (
                <p className="text-center py-6 text-slate-400">No serial numbers added yet</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-emerald-600 mb-2">Available: {availableSerials.length} | <span className="text-red-500">Sold: {soldSerials.length}</span></p>
                  {serialNumbers.map(sn => (
                    <div key={sn.id} className={`flex items-center justify-between px-3 py-2 rounded-lg ${sn.status === 'sold' ? 'bg-red-50' : 'bg-slate-50'}`}>
                      {editingSerial === sn.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input value={editSerialValue} onChange={e => setEditSerialValue(e.target.value)} className="flex-1 px-2 py-1 border border-slate-200 rounded text-sm" autoFocus />
                          <button onClick={() => saveSerialEdit(sn.id)} className="p-1 bg-emerald-500 text-white rounded"><Check size={14} /></button>
                          <button onClick={() => setEditingSerial(null)} className="p-1 bg-slate-400 text-white rounded"><X size={14} /></button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <Hash size={14} className={sn.status === 'sold' ? 'text-red-400' : 'text-orange-500'} />
                            <span className="text-sm font-mono font-medium">{sn.serial_number}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${sn.status === 'sold' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{sn.status}</span>
                          </div>
                          {sn.status === 'available' && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setEditingSerial(sn.id); setEditSerialValue(sn.serial_number); }} className="p-1 text-slate-400 hover:text-blue-600"><Edit2 size={14} /></button>
                              <button onClick={() => deleteSerial(sn.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stock List View Component
const StockListView = ({ products, editingStock, editForm, setEditForm, onEdit, onSave, onCancel, onManageSerials }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Product</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Current Stock</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Alert Level</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">MRP</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Selling</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {products.map((product) => {
              const isEditing = editingStock === product.id;
              const stockStatus = product.current_stock <= 0 ? 'out' : 
                product.current_stock <= (product.stock_alert_quantity || 5) ? 'low' : 'ok';
              
              return (
                <tr key={product.id} className="hover:bg-slate-50" data-testid={`stock-row-${product.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        {product.image ? (
                          <img src={product.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <Package className="text-slate-400" size={20} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{product.name}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-slate-500">SKU: {product.sku}</p>
                          {product.track_batch_expiry && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Batch</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.current_stock}
                        onChange={(e) => setEditForm({...editForm, current_stock: parseFloat(e.target.value) || 0})}
                        className="w-20 px-2 py-1 border border-violet-300 rounded text-right text-sm"
                      />
                    ) : (
                      <span className={`font-medium ${stockStatus === 'out' ? 'text-red-600' : stockStatus === 'low' ? 'text-amber-600' : 'text-slate-800'}`}>
                        {product.current_stock} {product.unit}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.stock_alert_quantity}
                        onChange={(e) => setEditForm({...editForm, stock_alert_quantity: parseFloat(e.target.value) || 0})}
                        className="w-20 px-2 py-1 border border-slate-200 rounded text-right text-sm"
                      />
                    ) : (
                      <span className="text-slate-600">{product.stock_alert_quantity || 0}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600">₹{product.mrp}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-800">₹{product.selling_price}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      stockStatus === 'out' ? 'bg-red-100 text-red-700' :
                      stockStatus === 'low' ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {stockStatus === 'out' ? 'Out of Stock' : stockStatus === 'low' ? 'Low Stock' : 'In Stock'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isEditing ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onSave(product.id)}
                          className="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={onCancel}
                          className="p-1.5 bg-slate-400 text-white rounded-lg hover:bg-slate-500"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onEdit(product)}
                          className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                          title="Edit Stock"
                        >
                          <Edit2 size={16} />
                        </button>
                        {product.has_serial_number && (
                          <button
                            onClick={() => onManageSerials(product)}
                            className="p-1.5 text-orange-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Manage Serial Numbers"
                            data-testid={`manage-serials-${product.id}`}
                          >
                            <Hash size={16} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {products.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Package size={48} className="mx-auto mb-3 opacity-50" />
          <p>No products found</p>
        </div>
      )}
    </div>
  );
};

// Batch Stock View Component
const BatchStockView = ({ products, allProducts, formatDate, expandedProducts, productBatches, loadingBatches, onToggleBatch, today }) => {
  const batchProducts = allProducts.filter(p => p.track_batch_expiry);
  const nonBatchProducts = allProducts.filter(p => !p.track_batch_expiry);

  return (
    <div className="space-y-4">
      {/* Batch Tracked Products */}
      {batchProducts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-2">
            <Layers size={16} className="text-blue-500" />
            Batch Tracked Products ({batchProducts.length})
          </h3>
          {batchProducts.map(product => (
            <div key={product.id} className="bg-white rounded-xl border border-blue-200 overflow-hidden">
              <div 
                onClick={() => onToggleBatch(product.id)}
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-blue-50 transition-colors"
                data-testid={`batch-product-${product.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Layers className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{product.name}</p>
                    <p className="text-xs text-slate-500">SKU: {product.sku} | Total Stock: {product.current_stock} {product.unit}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium">
                    Batch Tracked
                  </span>
                  {loadingBatches[product.id] ? (
                    <RefreshCw size={18} className="animate-spin text-slate-400" />
                  ) : expandedProducts[product.id] ? (
                    <ChevronUp size={20} className="text-slate-400" />
                  ) : (
                    <ChevronDown size={20} className="text-slate-400" />
                  )}
                </div>
              </div>
              
              {expandedProducts[product.id] && productBatches[product.id] && (
                <div className="border-t border-blue-200 bg-blue-50/50 p-4">
                  {productBatches[product.id].length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No batches found for this product</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-blue-200">
                            <th className="pb-2 font-medium text-slate-600">Batch Number</th>
                            <th className="pb-2 font-medium text-slate-600 text-right">Quantity</th>
                            <th className="pb-2 font-medium text-slate-600 text-right">Purchase Price</th>
                            <th className="pb-2 font-medium text-slate-600 text-right">Selling Price</th>
                            <th className="pb-2 font-medium text-slate-600 text-center">Expiry Date</th>
                            <th className="pb-2 font-medium text-slate-600 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-blue-100">
                          {productBatches[product.id].map(batch => {
                            const isExpired = batch.expiry_date && batch.expiry_date < today;
                            const isExpiringSoon = batch.expiry_date && !isExpired && 
                              new Date(batch.expiry_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                            
                            return (
                              <tr key={batch.id} className={isExpired ? 'bg-red-50' : ''}>
                                <td className="py-2 font-medium">{batch.batch_number}</td>
                                <td className="py-2 text-right">{batch.quantity}</td>
                                <td className="py-2 text-right">₹{batch.purchase_price?.toFixed(2)}</td>
                                <td className="py-2 text-right">₹{batch.selling_price?.toFixed(2)}</td>
                                <td className="py-2 text-center">
                                  {batch.expiry_date ? formatDate(batch.expiry_date) : '-'}
                                </td>
                                <td className="py-2 text-center">
                                  {isExpired ? (
                                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">Expired</span>
                                  ) : isExpiringSoon ? (
                                    <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">Expiring Soon</span>
                                  ) : batch.quantity > 0 ? (
                                    <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full">Active</span>
                                  ) : (
                                    <span className="px-2 py-0.5 bg-slate-400 text-white text-xs rounded-full">Out of Stock</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Non-Batch Products Summary */}
      {nonBatchProducts.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Package size={16} className="text-slate-400" />
            Regular Products ({nonBatchProducts.length})
          </h3>
          <p className="text-sm text-slate-500">
            {nonBatchProducts.length} products without batch tracking. Enable "Track Batch Number & Expiry" in product settings to track batches.
          </p>
        </div>
      )}

      {allProducts.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Package size={48} className="mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No products found</p>
        </div>
      )}
    </div>
  );
};

// Price Edit View Component
const PriceEditView = ({ products, editedProducts, saving, onPriceChange, onSave, calculateMargin, formatDateTime }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Product</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-600 uppercase">Cost</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-600 uppercase">MRP</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-600 uppercase">L1 (Sell)</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-600 uppercase">L2</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-600 uppercase">L3</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-600 uppercase">L4</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-600 uppercase">L5</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-600 uppercase">Margin</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-600 uppercase">Updated</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-600 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {products.map((product) => {
              const edited = editedProducts[product.id] || {};
              const cost = edited.base_price ?? product.base_price;
              const selling = edited.selling_price ?? product.selling_price;
              const margin = calculateMargin(cost, selling);
              const hasEdits = !!editedProducts[product.id];
              
              return (
                <tr 
                  key={product.id} 
                  className={`hover:bg-slate-50 ${hasEdits ? 'bg-violet-50' : ''}`}
                  data-testid={`price-row-${product.id}`}
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{product.name}</p>
                      <p className="text-xs text-slate-500">SKU: {product.sku}</p>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <input
                      type="number"
                      step="0.01"
                      value={edited.base_price ?? product.base_price ?? ''}
                      onChange={(e) => onPriceChange(product.id, 'base_price', e.target.value)}
                      className="w-20 text-right px-2 py-1 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <input
                      type="number"
                      step="0.01"
                      value={edited.mrp ?? product.mrp ?? ''}
                      onChange={(e) => onPriceChange(product.id, 'mrp', e.target.value)}
                      className="w-20 text-right px-2 py-1 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <input
                      type="number"
                      step="0.01"
                      value={edited.selling_price ?? product.selling_price ?? ''}
                      onChange={(e) => onPriceChange(product.id, 'selling_price', e.target.value)}
                      className="w-20 text-right px-2 py-1 border border-violet-300 rounded text-sm bg-violet-50 focus:ring-2 focus:ring-violet-500 focus:border-transparent font-medium"
                    />
                  </td>
                  {['selling_price_l2', 'selling_price_l3', 'selling_price_l4', 'selling_price_l5'].map(field => (
                    <td key={field} className="px-3 py-3">
                      <input
                        type="number"
                        step="0.01"
                        value={edited[field] ?? product[field] ?? ''}
                        onChange={(e) => onPriceChange(product.id, field, e.target.value)}
                        placeholder="-"
                        className="w-20 text-right px-2 py-1 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      />
                    </td>
                  ))}
                  <td className="px-3 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      margin >= 20 ? 'bg-emerald-100 text-emerald-700' :
                      margin >= 10 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {margin}%
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-slate-500">
                      {formatDateTime(product.price_updated_at || product.updated_at)}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {hasEdits && (
                      <button
                        onClick={() => onSave(product)}
                        disabled={saving}
                        className="p-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
                        title="Save this product"
                      >
                        <Save size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {products.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Package size={48} className="mx-auto mb-3 opacity-50" />
          <p>No products found</p>
        </div>
      )}
    </div>
  );
};

// Expired Products View Component
const ExpiredProductsView = ({ expiredProducts, formatDate, onRefresh }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-red-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="text-red-600" size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-red-800">Expired Products</h3>
            <p className="text-xs text-red-600">{expiredProducts.length} batch(es) have expired</p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 hover:bg-red-100 rounded-lg transition-colors"
          data-testid="refresh-expired-btn"
        >
          <RefreshCw size={18} className="text-red-600" />
        </button>
      </div>
      
      {expiredProducts.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
            <Check className="text-emerald-600" size={32} />
          </div>
          <p className="text-slate-600 font-medium">No expired products!</p>
          <p className="text-xs text-slate-400 mt-1">All batch-tracked products are within expiry date</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Product</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Batch Number</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Quantity</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Value</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Expiry Date</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Days Expired</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expiredProducts.map((item, index) => {
                const daysExpired = Math.floor(
                  (new Date() - new Date(item.expiry_date)) / (1000 * 60 * 60 * 24)
                );
                const value = (item.quantity || 0) * (item.purchase_price || 0);
                
                return (
                  <tr key={`expired-${index}`} className="hover:bg-red-50" data-testid={`expired-item-${index}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{item.product_name}</p>
                      <p className="text-xs text-slate-500">SKU: {item.sku}</p>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">{item.batch_number}</td>
                    <td className="px-4 py-3 text-right font-medium">{item.quantity}</td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium">₹{value.toFixed(2)}</td>
                    <td className="px-4 py-3 text-center text-red-600">{formatDate(item.expiry_date)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                        {daysExpired} days
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Raw Materials View Component
const RawMaterialsView = ({ materials, search, setSearch }) => {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search raw materials..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Material</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Current Stock</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Alert Level</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Cost Price</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {materials.map((material) => {
                const stockStatus = material.current_stock <= 0 ? 'out' : 
                  material.current_stock <= (material.stock_alert_quantity || 5) ? 'low' : 'ok';
                
                return (
                  <tr key={material.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Scissors className="text-amber-600" size={18} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{material.name}</p>
                          <p className="text-xs text-slate-500">SKU: {material.sku}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-medium ${stockStatus === 'out' ? 'text-red-600' : stockStatus === 'low' ? 'text-amber-600' : 'text-slate-800'}`}>
                        {material.current_stock} {material.unit}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">{material.stock_alert_quantity || 0}</td>
                    <td className="px-4 py-3 text-right text-slate-600">₹{material.base_price}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        stockStatus === 'out' ? 'bg-red-100 text-red-700' :
                        stockStatus === 'low' ? 'bg-amber-100 text-amber-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {stockStatus === 'out' ? 'Out of Stock' : stockStatus === 'low' ? 'Low Stock' : 'In Stock'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {materials.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Scissors size={48} className="mx-auto mb-3 opacity-50" />
            <p>No raw materials found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockPriceManagement;
